import {
  BulkItemApplicationService,
  CreateCanvasService,
  CreateDocumentService,
  FolderApplicationService,
  ItemMetadataApplicationService,
  RevisionApplicationService,
  SaveCanvasService,
  SaveDocumentService,
  TagApplicationService,
  TransferDocumentBlocksService,
  TrashApplicationService,
  prepareQuickCapture,
  type DocumentBlockTransferMode,
  type DocumentBlockTransferResult,
  type DocumentBlockTransferUndoResult,
  type DocumentBlockTransferUndoToken,
  type RevisionPreview,
  type RevisionRestoreResult,
  type RevisionSummary,
  type BulkItemMutation,
  type BulkMutationResult,
} from "@note-app/application";
import {
  CURRENT_CONTENT_SCHEMA_VERSION,
  type EntityId,
  type DomainCanvas,
  type DomainBlock,
  type DomainDocument,
  type ItemKind,
  type KnowledgeItem,
  type PageResult,
  type ReferenceTarget,
  createReferenceUri,
  parseReferenceUri,
} from "@note-app/contracts";
import { createEmptyCanvas } from "@note-app/canvas";
import type { Clock, Folder, IdGenerator, Tag } from "@note-app/domain";
import type { PlatformRuntime } from "@note-app/platform-runtime";
import { EntityNotFoundError } from "@note-app/storage";
import { validateDomainDocument } from "notion-style-editor/document-boundary";

export interface DocumentViewModel {
  readonly item: KnowledgeItem;
  readonly revision: number;
  readonly document: DomainDocument;
  readonly plainText: string;
  readonly readOnly: boolean;
  readonly readOnlyReason?: string;
}

export interface CanvasViewModel {
  readonly item: KnowledgeItem;
  readonly revision: number;
  readonly canvas: DomainCanvas;
  readonly readOnly: boolean;
}

export type KnowledgeCollection =
  | "all"
  | "recent"
  | "favorites"
  | "trash";

export interface KnowledgeCollectionQuery {
  readonly collection?: KnowledgeCollection;
  readonly kind?: ItemKind;
  readonly folderId?: EntityId;
  readonly tagId?: EntityId;
}

export interface CatalogSnapshot {
  readonly folders: readonly Folder[];
  readonly tags: readonly Tag[];
  readonly trashedFolders: readonly Folder[];
  readonly trashedTags: readonly Tag[];
  readonly itemCountByFolder: ReadonlyMap<EntityId, number>;
  readonly itemCountByTag: ReadonlyMap<EntityId, number>;
}

export interface ItemMetadataInput {
  readonly folderId: EntityId | null;
  readonly tagIds: readonly EntityId[];
  readonly favorite: boolean;
  readonly icon?: string | null;
  readonly properties?: Readonly<Record<string, import("@note-app/contracts").JsonValue>>;
}

export interface ResolvedKnowledgeReference {
  readonly item: KnowledgeItem;
  readonly target: Extract<ReferenceTarget, { kind: "item" | "block" }>;
  readonly title: string;
  readonly subtitle?: string;
}

export interface KnowledgeRelationHit {
  readonly item: KnowledgeItem;
  readonly kind: "backlink" | "unlinked" | "outgoing" | "broken";
  readonly label: string;
  readonly snippet: string;
  readonly blockId?: EntityId;
  readonly target?: Extract<ReferenceTarget, { kind: "item" | "block" }>;
  readonly sourceText?: string;
}

export interface KnowledgeRelationsSnapshot {
  readonly backlinks: readonly KnowledgeRelationHit[];
  readonly unlinkedMentions: readonly KnowledgeRelationHit[];
  readonly outgoing: readonly KnowledgeRelationHit[];
  readonly broken: readonly KnowledgeRelationHit[];
}

export interface KnowledgeSearchHit {
  readonly item: KnowledgeItem;
  readonly blockId?: EntityId;
  readonly snippet: string;
  readonly locationLabel: string;
}

const FAVORITE_PROPERTY = "fengyue.favorite";
const PAGE_SIZE = 200;

export class KnowledgeAppViewModel {
  private projectionCache:
    | {
        readonly expiresAt: number;
        readonly value: Promise<KnowledgeProjection[]>;
      }
    | undefined;

  public constructor(
    public readonly runtime: PlatformRuntime,
    private readonly ids: IdGenerator = {
      next: () => crypto.randomUUID(),
    },
    private readonly clock: Clock = {
      now: () => Date.now(),
    },
  ) {}

  async list(kind?: ItemKind): Promise<readonly KnowledgeItem[]> {
    return this.listCollection({ ...(kind === undefined ? {} : { kind }) });
  }

  async listCollection(
    query: KnowledgeCollectionQuery = {},
  ): Promise<readonly KnowledgeItem[]> {
    const collection = query.collection ?? "all";
    const items = await collectPages((cursor) =>
      this.runtime.unitOfWork.repositories.items.list({
        deletion: collection === "trash" ? "trash" : "active",
        limit: PAGE_SIZE,
        ...(query.kind === undefined ? {} : { kind: query.kind }),
        ...(query.folderId === undefined ? {} : { folderId: query.folderId }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
    );
    const filtered = items.filter((item) => {
      if (query.tagId !== undefined && !item.tags.includes(query.tagId)) {
        return false;
      }
      if (collection === "favorites" && !isFavorite(item)) return false;
      return true;
    });
    const ordered = filtered.sort(compareRecentlyUpdated);
    return collection === "recent" ? ordered.slice(0, 100) : ordered;
  }

  async search(query: string): Promise<readonly KnowledgeItem[]> {
    const preparedQuery = prepareSearchQuery(query);
    if (!preparedQuery) return this.list();
    const projections = await this.buildKnowledgeProjections();
    return projections
      .map((projection) => ({
        projection,
        score: scoreKnowledgeProjection(projection, preparedQuery),
      }))
      .filter((entry) => entry.score > 0)
      .sort((left, right) =>
        right.score - left.score ||
        right.projection.item.updatedAt - left.projection.item.updatedAt,
      )
      .map((entry) => entry.projection.item);
  }

  async searchDetailed(query: string): Promise<readonly KnowledgeSearchHit[]> {
    const preparedQuery = prepareSearchQuery(query);
    if (!preparedQuery) return [];
    const projections = await this.buildKnowledgeProjections();
    const hits: Array<{ readonly hit: KnowledgeSearchHit; readonly score: number }> = [];
    for (const projection of projections) {
      const titleMatch = scoreSearchText(
        projection.item.title,
        preparedQuery,
        700,
      );
      if (titleMatch) {
        hits.push({
          hit: {
            item: projection.item,
            snippet: projection.item.title,
            locationLabel: "标题",
          },
          score: titleMatch.score,
        });
      }
      for (const entry of projection.entries) {
        const match = scoreSearchText(entry.text, preparedQuery, 120);
        if (!match) continue;
        hits.push({
          hit: {
            item: projection.item,
            ...(entry.blockId ? { blockId: entry.blockId } : {}),
            snippet: contextSnippet(entry.text, match.index, match.length),
            locationLabel: entry.locationLabel,
          },
          score: match.score,
        });
      }
    }
    hits.sort((left, right) =>
      right.score - left.score ||
      right.hit.item.updatedAt - left.hit.item.updatedAt,
    );
    const hitsPerItem = new Map<EntityId, number>();
    return hits
      .filter(({ hit }) => {
        const count = hitsPerItem.get(hit.item.id) ?? 0;
        if (count >= 3) return false;
        hitsPerItem.set(hit.item.id, count + 1);
        return true;
      })
      .slice(0, 80)
      .map((entry) => entry.hit);
  }

  async analyzeKnowledgeRelations(itemId: EntityId): Promise<KnowledgeRelationsSnapshot> {
    const projections = await this.buildKnowledgeProjections();
    const current = projections.find((entry) => entry.item.id === itemId);
    if (!current) throw new EntityNotFoundError("item", itemId);
    const itemById = new Map(projections.map((entry) => [entry.item.id, entry.item]));
    const blockOwner = new Map<EntityId, KnowledgeItem>();
    for (const projection of projections) for (const entry of projection.entries) if (entry.blockId) blockOwner.set(entry.blockId, projection.item);
    const targetBlockIds = new Set(current.entries.flatMap((entry) => entry.blockId ? [entry.blockId] : []));
    const backlinks: KnowledgeRelationHit[] = [];
    const unlinkedMentions: KnowledgeRelationHit[] = [];
    const outgoing: KnowledgeRelationHit[] = [];
    const broken: KnowledgeRelationHit[] = [];
    const targetTitle = current.item.title.trim().toLocaleLowerCase();

    for (const projection of projections) {
      let linksToCurrent = false;
      for (const entry of projection.entries) {
        for (const target of entry.references) {
          const resolved = target.kind === "item" ? itemById.get(target.id) : blockOwner.get(target.id);
          const relation: KnowledgeRelationHit = {
            item: projection.item,
            kind: "outgoing",
            label: resolved?.title ?? `${target.kind === "item" ? "页面" : "内容块"}已失效`,
            snippet: entry.text.slice(0, 180) || entry.locationLabel,
            ...(entry.blockId ? { blockId: entry.blockId } : {}),
            target,
            ...(entry.repairableReferenceKeys.has(referenceKey(target))
              ? { sourceText: createReferenceUri(target) }
              : {}),
          };
          if (projection.item.id === itemId) {
            if (resolved) outgoing.push(relation);
            else broken.push({ ...relation, kind: "broken" });
          }
          if (projection.item.id !== itemId && (target.kind === "item" ? target.id === itemId : targetBlockIds.has(target.id))) {
            linksToCurrent = true;
            backlinks.push({ ...relation, kind: "backlink", label: projection.item.title });
          }
        }
      }
      if (projection.item.id !== itemId && targetTitle && !linksToCurrent) {
        const mention = projection.entries.find((entry) => entry.text.toLocaleLowerCase().includes(targetTitle));
        if (mention) unlinkedMentions.push({ item: projection.item, kind: "unlinked", label: projection.item.title, snippet: contextSnippet(mention.text, mention.text.toLocaleLowerCase().indexOf(targetTitle), targetTitle.length), ...(mention.blockId ? { blockId: mention.blockId } : {}) });
      }
    }
    return { backlinks: uniqueRelationHits(backlinks), unlinkedMentions: uniqueRelationHits(unlinkedMentions), outgoing: uniqueRelationHits(outgoing), broken: uniqueRelationHits(broken) };
  }

  async quickCapture(input: {
    title: string;
    body: string;
    mode?: "note" | "task" | "link";
  }): Promise<KnowledgeItem> {
    const prepared = prepareQuickCapture({
      title: input.title,
      body: input.body,
      ...(input.mode === undefined ? {} : { mode: input.mode }),
      fallbackTitle: "快速记录",
      nextId: () => this.ids.next(),
    });
    const document = assertEditorDocument(prepared.document);
    const created = await new CreateDocumentService({
      unitOfWork: this.runtime.unitOfWork,
      ids: this.ids,
      clock: this.clock,
    }).execute({
      title: prepared.title,
      document,
    });
    this.invalidateKnowledgeProjections();
    return created.item;
  }

  async createDocument(title = "无标题"): Promise<KnowledgeItem> {
    return this.quickCapture({ title, body: "" });
  }

  async createCanvas(title = "新画板"): Promise<KnowledgeItem> {
    const created = await new CreateCanvasService({
      unitOfWork: this.runtime.unitOfWork,
      ids: this.ids,
      clock: this.clock,
    }).execute({ title, canvas: createEmptyCanvas("freeform") });
    this.invalidateKnowledgeProjections();
    return created.item;
  }

  async loadCatalog(): Promise<CatalogSnapshot> {
    const repositories = this.runtime.unitOfWork.repositories;
    const [folders, tags, items] = await Promise.all([
      collectPages((cursor) =>
        repositories.folders.list({
          deletion: "all",
          limit: PAGE_SIZE,
          ...(cursor === undefined ? {} : { cursor }),
        }),
      ),
      collectPages((cursor) =>
        repositories.tags.list({
          deletion: "all",
          limit: PAGE_SIZE,
          ...(cursor === undefined ? {} : { cursor }),
        }),
      ),
      collectPages((cursor) =>
        repositories.items.list({
          deletion: "active",
          limit: PAGE_SIZE,
          ...(cursor === undefined ? {} : { cursor }),
        }),
      ),
    ]);
    const itemCountByFolder = new Map<EntityId, number>();
    const itemCountByTag = new Map<EntityId, number>();
    for (const item of items) {
      if (item.folderId !== undefined) {
        itemCountByFolder.set(
          item.folderId,
          (itemCountByFolder.get(item.folderId) ?? 0) + 1,
        );
      }
      for (const tagId of item.tags) {
        itemCountByTag.set(tagId, (itemCountByTag.get(tagId) ?? 0) + 1);
      }
    }
    return {
      folders: folders.filter(isActive).sort(compareCatalogPosition),
      tags: tags.filter(isActive).sort(compareCatalogName),
      trashedFolders: folders.filter(isDeleted).sort(compareCatalogName),
      trashedTags: tags.filter(isDeleted).sort(compareCatalogName),
      itemCountByFolder,
      itemCountByTag,
    };
  }

  async updateItemMetadata(
    item: KnowledgeItem,
    input: ItemMetadataInput,
  ): Promise<KnowledgeItem> {
    return this.withProjectionInvalidation(() =>
      new ItemMetadataApplicationService(
        this.runtime.unitOfWork,
        this.clock,
      ).execute({
        itemId: item.id,
        expectedRevision: item.revision,
        patch: {
          folderId: input.folderId,
          tagIds: input.tagIds,
          favorite: input.favorite,
          ...(!("icon" in input) ? {} : { icon: input.icon ?? null }),
          ...(input.properties === undefined ? {} : { properties: input.properties }),
        },
      }),
    );
  }

  async setFavorite(
    item: KnowledgeItem,
    favorite: boolean,
  ): Promise<KnowledgeItem> {
    return this.withProjectionInvalidation(() =>
      new ItemMetadataApplicationService(
        this.runtime.unitOfWork,
        this.clock,
      ).execute({
        itemId: item.id,
        expectedRevision: item.revision,
        patch: { favorite },
      }),
    );
  }

  async bulkUpdateItems(
    items: readonly KnowledgeItem[],
    mutation: BulkItemMutation,
  ): Promise<BulkMutationResult> {
    return this.withProjectionInvalidation(() =>
      new BulkItemApplicationService(
        this.runtime.unitOfWork,
        this.clock,
      ).execute({
        targets: items.map((item) => ({ itemId: item.id, expectedRevision: item.revision })),
        mutation,
        policy: "best-effort",
      }),
    );
  }

  async exportItems(items: readonly KnowledgeItem[]): Promise<void> {
    const entries = [];
    for (const item of items) {
      if (item.kind === "document") {
        const stored = await this.runtime.unitOfWork.repositories.documents.load(item.id);
        entries.push({ item, content: stored?.document ?? null });
      } else {
        const stored = await this.runtime.unitOfWork.repositories.canvases.load(item.id);
        entries.push({ item, content: stored?.canvas ?? null });
      }
    }
    const bytes = new TextEncoder().encode(JSON.stringify({ format: "fengyue-bulk-export", version: 1, exportedAt: this.clock.now(), entries }, null, 2));
    await this.runtime.files.exportFile({
      bytes,
      fileName: `fengyue-selection-${new Date(this.clock.now()).toISOString().slice(0, 10)}.json`,
      mimeType: "application/json;charset=utf-8",
      title: `导出 ${items.length} 项丰月笔记`,
    });
  }

  async moveItemToTrash(item: KnowledgeItem): Promise<KnowledgeItem> {
    const result = await new BulkItemApplicationService(
      this.runtime.unitOfWork,
      this.clock,
    ).execute({
      targets: [{ itemId: item.id, expectedRevision: item.revision }],
      mutation: { kind: "trash" },
    });
    this.invalidateKnowledgeProjections();
    return requireMutationResult(result.succeeded[0], item.id);
  }

  async restoreItem(item: KnowledgeItem): Promise<KnowledgeItem> {
    const result = await new BulkItemApplicationService(
      this.runtime.unitOfWork,
      this.clock,
    ).execute({
      targets: [{ itemId: item.id, expectedRevision: item.revision }],
      mutation: { kind: "restore" },
    });
    this.invalidateKnowledgeProjections();
    return requireMutationResult(result.succeeded[0], item.id);
  }

  async purgeItem(item: KnowledgeItem): Promise<void> {
    await new TrashApplicationService(
      this.runtime.unitOfWork,
      this.clock,
    ).purgeItem({ itemId: item.id, expectedRevision: item.revision });
    this.invalidateKnowledgeProjections();
  }

  async emptyTrash(): Promise<number> {
    const result = await new TrashApplicationService(
      this.runtime.unitOfWork,
      this.clock,
    ).empty();
    this.invalidateKnowledgeProjections();
    return result.purged.length;
  }

  createFolder(input: {
    name: string;
    parentId?: EntityId;
  }): Promise<Folder> {
    return new FolderApplicationService(this.catalogDependencies()).executeCreate(
      input,
    );
  }

  updateFolder(input: {
    folder: Folder;
    name: string;
    parentId: EntityId | null;
  }): Promise<Folder> {
    return new FolderApplicationService(this.catalogDependencies()).executeUpdate({
      folderId: input.folder.id,
      expectedRevision: input.folder.revision,
      name: input.name,
      parentId: input.parentId,
    });
  }

  trashFolder(folder: Folder): Promise<Folder> {
    return new FolderApplicationService(this.catalogDependencies()).executeTrash({
      folderId: folder.id,
      expectedRevision: folder.revision,
    });
  }

  restoreFolder(folder: Folder): Promise<Folder> {
    return new FolderApplicationService(this.catalogDependencies()).executeRestore({
      folderId: folder.id,
      expectedRevision: folder.revision,
    });
  }

  createTag(input: { name: string; color?: string }): Promise<Tag> {
    return new TagApplicationService(this.catalogDependencies()).executeCreate(input);
  }

  updateTag(input: { tag: Tag; name: string; color: string | null }): Promise<Tag> {
    return new TagApplicationService(this.catalogDependencies()).executeUpdate({
      tagId: input.tag.id,
      expectedRevision: input.tag.revision,
      name: input.name,
      color: input.color,
    });
  }

  trashTag(tag: Tag): Promise<Tag> {
    return new TagApplicationService(this.catalogDependencies()).executeTrash({
      tagId: tag.id,
      expectedRevision: tag.revision,
    });
  }

  restoreTag(tag: Tag): Promise<Tag> {
    return new TagApplicationService(this.catalogDependencies()).executeRestore({
      tagId: tag.id,
      expectedRevision: tag.revision,
    });
  }

  async loadDocument(itemId: string): Promise<DocumentViewModel> {
    const [item, stored] = await Promise.all([
      this.runtime.unitOfWork.repositories.items.get(itemId),
      this.runtime.unitOfWork.repositories.documents.load(itemId),
    ]);
    if (!item || !stored) throw new EntityNotFoundError("document", itemId);
    const document = assertEditorDocument(stored.document);
    const storageReadOnly =
      this.runtime.capabilities.structuredStorage.support === "degraded";
    return {
      item,
      revision: stored.revision,
      document,
      plainText: documentToPlainText(document),
      readOnly: storageReadOnly,
      ...(storageReadOnly
        ? {
            readOnlyReason:
              "当前数据由更高版本创建，已进入只读保护。",
          }
        : {}),
    };
  }

  async saveDocument(input: {
    itemId: EntityId;
    document: DomainDocument;
    expectedRevision: number;
  }): Promise<DocumentViewModel> {
    const document = assertEditorDocument(input.document);
    const receipt = await new SaveDocumentService(
      this.runtime.unitOfWork,
      this.clock,
    ).execute({
      itemId: input.itemId,
      expectedRevision: input.expectedRevision,
      document,
    });
    const item = await this.runtime.unitOfWork.repositories.items.get(
      input.itemId,
    );
    if (!item) throw new EntityNotFoundError("document", input.itemId);
    this.invalidateKnowledgeProjections();
    return {
      item,
      revision: receipt.revision,
      document,
      plainText: documentToPlainText(document),
      readOnly:
        this.runtime.capabilities.structuredStorage.support === "degraded",
      ...(this.runtime.capabilities.structuredStorage.support === "degraded"
        ? { readOnlyReason: "当前数据由更高版本创建，已进入只读保护。" }
        : {}),
    };
  }

  /**
   * Performs a cross-document block transfer against fresh revisions. The
   * application service commits both documents atomically and returns a
   * revision-guarded token that can be offered as a one-click undo action.
   */
  async transferDocumentBlocks(input: {
    sourceDocumentId: EntityId;
    targetDocumentId: EntityId;
    blockIds: readonly EntityId[];
    mode: DocumentBlockTransferMode;
  }): Promise<DocumentBlockTransferResult> {
    const [source, target] = await Promise.all([
      this.loadDocument(input.sourceDocumentId),
      this.loadDocument(input.targetDocumentId),
    ]);
    return this.withProjectionInvalidation(() =>
      new TransferDocumentBlocksService(
        this.runtime.unitOfWork,
        this.clock,
        this.ids,
      ).execute({
        ...input,
        sourceExpectedRevision: source.revision,
        targetExpectedRevision: target.revision,
      }),
    );
  }

  undoDocumentBlockTransfer(
    token: DocumentBlockTransferUndoToken,
  ): Promise<DocumentBlockTransferUndoResult> {
    return this.withProjectionInvalidation(() =>
      new TransferDocumentBlocksService(
        this.runtime.unitOfWork,
        this.clock,
        this.ids,
      ).undo(token),
    );
  }

  async renameDocument(input: {
    itemId: EntityId;
    title: string;
    expectedRevision: number;
  }): Promise<KnowledgeItem> {
    const current = await this.runtime.unitOfWork.repositories.items.get(
      input.itemId,
    );
    if (!current) throw new EntityNotFoundError("document", input.itemId);
    return this.withProjectionInvalidation(() =>
      this.runtime.unitOfWork.repositories.items.update(
        input.itemId,
        { title: input.title.trim() || "无标题" },
        input.expectedRevision,
        Math.max(this.clock.now(), current.updatedAt),
      ),
    );
  }

  listRevisions(itemId: EntityId): Promise<readonly RevisionSummary[]> {
    return this.revisionService().list(itemId);
  }

  previewRevision(
    itemId: EntityId,
    revisionId: EntityId,
  ): Promise<RevisionPreview> {
    return this.revisionService().preview({ itemId, revisionId });
  }

  restoreRevision(input: {
    itemId: EntityId;
    revisionId: EntityId;
    expectedRevision: number;
  }): Promise<RevisionRestoreResult> {
    return this.withProjectionInvalidation(() =>
      this.revisionService().restore(input),
    );
  }

  async resolveReferenceTarget(
    target: ReferenceTarget,
  ): Promise<ResolvedKnowledgeReference | null> {
    if (target.kind === "attachment") return null;
    if (target.kind === "item") {
      const item = await this.runtime.unitOfWork.repositories.items.get(
        target.id,
      );
      return item
        ? { item, target, title: item.title, subtitle: itemKindLabel(item.kind) }
        : null;
    }
    const items = await this.list("document");
    for (const item of items) {
      const stored =
        await this.runtime.unitOfWork.repositories.documents.load(item.id);
      const block = stored ? findBlock(stored.document.blocks, target.id) : null;
      if (block) {
        return {
          item,
          target,
          title: blockToLabel(block),
          subtitle: item.title,
        };
      }
    }
    return null;
  }

  async loadCanvas(itemId: string): Promise<CanvasViewModel> {
    const [item, stored] = await Promise.all([
      this.runtime.unitOfWork.repositories.items.get(itemId),
      this.runtime.unitOfWork.repositories.canvases.load(itemId),
    ]);
    if (!item || !stored) throw new EntityNotFoundError("canvas", itemId);
    return {
      item,
      revision: stored.revision,
      canvas: stored.canvas,
      readOnly:
        this.runtime.capabilities.structuredStorage.support === "degraded",
    };
  }

  async moveCanvasNode(
    view: CanvasViewModel,
    nodeId: string,
    delta: { x: number; y: number },
  ): Promise<CanvasViewModel> {
    const canvas: DomainCanvas = {
      ...view.canvas,
      nodes: view.canvas.nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              geometry: {
                ...node.geometry,
                x: node.geometry.x + delta.x,
                y: node.geometry.y + delta.y,
              },
            }
          : node,
      ),
    };
    return this.saveCanvas({
      itemId: view.item.id,
      expectedRevision: view.revision,
      canvas,
    });
  }

  async saveCanvas(input: {
    itemId: EntityId;
    canvas: DomainCanvas;
    expectedRevision: number;
    title?: string;
  }): Promise<CanvasViewModel> {
    const receipt = await new SaveCanvasService(
      this.runtime.unitOfWork,
      this.clock,
    ).execute(input);
    const item = await this.runtime.unitOfWork.repositories.items.get(
      input.itemId,
    );
    if (!item) throw new EntityNotFoundError("canvas", input.itemId);
    this.invalidateKnowledgeProjections();
    return {
      revision: receipt.revision,
      item,
      canvas: input.canvas,
      readOnly:
        this.runtime.capabilities.structuredStorage.support === "degraded",
    };
  }

  private async buildKnowledgeProjections(): Promise<KnowledgeProjection[]> {
    const now = Date.now();
    if (this.projectionCache && this.projectionCache.expiresAt > now) {
      return this.projectionCache.value;
    }
    const value = this.loadKnowledgeProjections();
    this.projectionCache = { expiresAt: now + 1_500, value };
    try {
      return await value;
    } catch (cause) {
      if (this.projectionCache?.value === value) this.projectionCache = undefined;
      throw cause;
    }
  }

  private invalidateKnowledgeProjections(): void {
    this.projectionCache = undefined;
  }

  private async withProjectionInvalidation<T>(
    operation: () => Promise<T>,
  ): Promise<T> {
    const result = await operation();
    this.invalidateKnowledgeProjections();
    return result;
  }

  private async loadKnowledgeProjections(): Promise<KnowledgeProjection[]> {
    const items = await this.list();
    return Promise.all(items.map(async (item): Promise<KnowledgeProjection> => {
      if (item.kind === "document") {
        const stored = await this.runtime.unitOfWork.repositories.documents.load(item.id);
        const entries: KnowledgeProjectionEntry[] = [];
        if (stored) collectProjectionBlocks(stored.document.blocks, entries);
        return { item, entries };
      }
      const stored = await this.runtime.unitOfWork.repositories.canvases.load(item.id);
      return {
        item,
        entries: stored?.canvas.nodes.map((node) => {
          const references = collectReferences(node.props);
          if (node.reference?.kind === "item" || node.reference?.kind === "block") references.push(node.reference);
          return {
            blockId: node.id,
            text: searchableBlockProps(node.props),
            locationLabel: "画板节点",
            references,
            repairableReferenceKeys: new Set<string>(),
          };
        }) ?? [],
      };
    }));
  }

  private catalogDependencies() {
    return {
      unitOfWork: this.runtime.unitOfWork,
      ids: this.ids,
      clock: this.clock,
    };
  }

  private revisionService(): RevisionApplicationService {
    return new RevisionApplicationService(this.runtime.unitOfWork, this.clock);
  }
}

export function documentToPlainText(document: DomainDocument): string {
  return document.blocks
    .map((block) =>
      typeof block.content === "string"
        ? block.content
        : JSON.stringify(block.content),
    )
    .join("\n\n");
}

interface KnowledgeProjectionEntry {
  readonly blockId?: EntityId;
  readonly text: string;
  readonly locationLabel: string;
  readonly references: Extract<ReferenceTarget, { kind: "item" | "block" }>[];
  readonly repairableReferenceKeys: ReadonlySet<string>;
}

interface KnowledgeProjection {
  readonly item: KnowledgeItem;
  readonly entries: KnowledgeProjectionEntry[];
}

interface PreparedSearchQuery {
  readonly normalized: string;
  readonly terms: readonly string[];
}

interface SearchTextMatch {
  readonly score: number;
  readonly index: number;
  readonly length: number;
}

function prepareSearchQuery(value: string): PreparedSearchQuery | undefined {
  const normalized = normalizeSearchText(value).trim();
  if (!normalized) return undefined;
  return {
    normalized,
    terms: [...new Set(normalized.split(/\s+/u).filter(Boolean))],
  };
}

function normalizeSearchText(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase();
}

function scoreKnowledgeProjection(
  projection: KnowledgeProjection,
  query: PreparedSearchQuery,
): number {
  const titleScore =
    scoreSearchText(projection.item.title, query, 700)?.score ?? 0;
  const contentScore = projection.entries.reduce(
    (highest, entry) =>
      Math.max(highest, scoreSearchText(entry.text, query, 120)?.score ?? 0),
    0,
  );
  return Math.max(titleScore, contentScore);
}

function scoreSearchText(
  value: string,
  query: PreparedSearchQuery,
  baseScore: number,
): SearchTextMatch | undefined {
  const normalized = normalizeSearchText(value);
  if (!query.terms.every((term) => normalized.includes(term))) {
    return undefined;
  }

  const phraseIndex = normalized.indexOf(query.normalized);
  if (phraseIndex >= 0) {
    return {
      score:
        baseScore +
        260 +
        (phraseIndex === 0 ? 90 : 0) +
        (normalized === query.normalized ? 120 : 0),
      index: phraseIndex,
      length: query.normalized.length,
    };
  }

  const firstTerm = query.terms
    .map((term) => ({ term, index: normalized.indexOf(term) }))
    .sort((left, right) => left.index - right.index)[0];
  if (!firstTerm) return undefined;
  return {
    score: baseScore + 80 + query.terms.length * 18,
    index: firstTerm.index,
    length: firstTerm.term.length,
  };
}

function collectProjectionBlocks(blocks: readonly DomainBlock[], output: KnowledgeProjectionEntry[]): void {
  for (const block of blocks) {
    const contentReferences = collectReferences(block.content);
    output.push({
      blockId: block.id,
      text: [jsonPlainText(block.content), searchableBlockProps(block.props)]
        .filter(Boolean)
        .join(" "),
      locationLabel: blockLocationLabel(block.type),
      references: dedupeReferences([
        ...contentReferences,
        ...collectReferences(block.props),
      ]),
      repairableReferenceKeys: new Set(contentReferences.map(referenceKey)),
    });
    collectProjectionBlocks(block.children, output);
  }
}

const searchablePropertyNames = new Set([
  "alt",
  "caption",
  "description",
  "label",
  "language",
  "name",
  "source",
  "text",
  "title",
  "url",
]);

function searchableBlockProps(props: DomainBlock["props"]): string {
  return Object.entries(props)
    .filter(([key]) => searchablePropertyNames.has(key))
    .map(([, value]) => jsonPlainText(value))
    .filter(Boolean)
    .join(" ");
}

function blockLocationLabel(type: string): string {
  const labels: Readonly<Record<string, string>> = {
    paragraph: "正文",
    heading: "标题块",
    bulletListItem: "无序列表",
    numberedListItem: "有序列表",
    checkListItem: "任务",
    quote: "引用",
    codeBlock: "代码块",
    table: "表格",
    image: "图片说明",
    video: "视频说明",
    audio: "音频说明",
    file: "附件",
    bookmark: "网页书签",
    callout: "提示块",
    toggle: "折叠内容",
    columns: "分栏",
    math: "公式",
    tableOfContents: "目录",
  };
  return labels[type] ?? "内容块";
}

function referenceKey(
  value: Extract<ReferenceTarget, { kind: "item" | "block" }>,
): string {
  return `${value.kind}:${value.id}`;
}

function collectReferences(value: unknown, output: Extract<ReferenceTarget, { kind: "item" | "block" }>[] = []): Extract<ReferenceTarget, { kind: "item" | "block" }>[] {
  if (typeof value === "string") {
    const direct = parseReferenceUri(value);
    if (direct?.kind === "item" || direct?.kind === "block") output.push(direct);
    for (const match of value.matchAll(/note:\/\/(item|block)\/[^\s"'<>]+/gu)) {
      const target = parseReferenceUri(match[0]);
      if (target?.kind === "item" || target?.kind === "block") output.push(target);
    }
  } else if (Array.isArray(value)) {
    value.forEach((entry) => collectReferences(entry, output));
  } else if (value && typeof value === "object") {
    Object.values(value as Record<string, unknown>).forEach((entry) => collectReferences(entry, output));
  }
  return dedupeReferences(output);
}

function dedupeReferences(values: readonly Extract<ReferenceTarget, { kind: "item" | "block" }>[]): Extract<ReferenceTarget, { kind: "item" | "block" }>[] {
  const seen = new Set<string>();
  return values.filter((value) => { const key = `${value.kind}:${value.id}`; if (seen.has(key)) return false; seen.add(key); return true; });
}

function jsonPlainText(value: unknown): string {
  if (typeof value === "string") return value.startsWith("note://") ? "" : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(jsonPlainText).filter(Boolean).join(" ");
  if (!value || typeof value !== "object") return "";
  const record = value as Record<string, unknown>;
  if (typeof record.text === "string") return record.text;
  return Object.entries(record).filter(([key]) => !["href", "url", "id"].includes(key)).map(([, entry]) => jsonPlainText(entry)).filter(Boolean).join(" ");
}

function contextSnippet(text: string, index: number, length: number): string {
  const start = Math.max(0, index - 48);
  const end = Math.min(text.length, index + length + 72);
  return `${start > 0 ? "…" : ""}${text.slice(start, end).trim()}${end < text.length ? "…" : ""}`;
}

function uniqueRelationHits(hits: readonly KnowledgeRelationHit[]): KnowledgeRelationHit[] {
  const seen = new Set<string>();
  return hits.filter((hit) => { const key = `${hit.kind}:${hit.item.id}:${hit.blockId ?? ""}:${hit.target ? `${hit.target.kind}:${hit.target.id}` : ""}`; if (seen.has(key)) return false; seen.add(key); return true; });
}

function findBlock(
  blocks: readonly DomainBlock[],
  blockId: EntityId,
): DomainBlock | null {
  for (const block of blocks) {
    if (block.id === blockId) return block;
    const nested = findBlock(block.children, blockId);
    if (nested) return nested;
  }
  return null;
}

function blockToLabel(block: DomainBlock): string {
  if (typeof block.content === "string") {
    const label = block.content.trim();
    if (label) return label.slice(0, 80);
  }
  return `${block.type} 块`;
}

function itemKindLabel(kind: ItemKind): string {
  return kind === "document" ? "文档" : "画板";
}

function isFavorite(item: KnowledgeItem): boolean {
  return item.properties[FAVORITE_PROPERTY] === true;
}

function compareRecentlyUpdated(left: KnowledgeItem, right: KnowledgeItem): number {
  return right.updatedAt - left.updatedAt || left.id.localeCompare(right.id);
}

function compareCatalogName(
  left: { name: string; id: EntityId },
  right: { name: string; id: EntityId },
): number {
  return left.name.localeCompare(right.name, "zh-CN") || left.id.localeCompare(right.id);
}

function compareCatalogPosition(left: Folder, right: Folder): number {
  return left.position - right.position || compareCatalogName(left, right);
}

function isActive<T extends { deletedAt?: number }>(value: T): boolean {
  return value.deletedAt === undefined;
}

function isDeleted<T extends { deletedAt?: number }>(value: T): boolean {
  return value.deletedAt !== undefined;
}

async function collectPages<T>(
  load: (cursor?: string) => Promise<PageResult<T>>,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await load(cursor);
    items.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor !== undefined);
  return items;
}

function requireMutationResult(
  item: KnowledgeItem | undefined,
  itemId: EntityId,
): KnowledgeItem {
  if (item === undefined) {
    throw new EntityNotFoundError("item", itemId);
  }
  return item;
}

function assertEditorDocument(document: unknown): DomainDocument {
  const result = validateDomainDocument(document);
  if (!result.ok) throw result.error;
  return result.value;
}
